import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class F1120lSchFService {

  constructor(private http: HttpClient) { }

  /**
   * Fetch Schedule F records from pivot form JSON
   * @param baseUrl Base URL from GENERAL_CONFIG
   * @param params Request parameters including action_id (603), tax_year, scenario, jcd_key, combination_key
   * @returns Observable with Schedule F form data
   */
  fetchSchFrecords(baseUrl: string, params: any): Observable<any> {
    const url = `${baseUrl}/loadPivotFormJson`;
    
    // Convert params to URLSearchParams for form-urlencoded format
    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }

  /**
   * Fetch Schedule F import batches
   * @param baseUrl Base URL from GENERAL_CONFIG
   * @param params Request parameters including client_key, process_name, status
   * @returns Observable with batch list data
   */
  fetchSchFimports(baseUrl: string, params: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=wwledr`;
    
    // Convert params to URLSearchParams for form-urlencoded format
    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }

  /**
   * Load transactions for a specific batch
   * @param baseUrl Base URL from GENERAL_CONFIG
   * @param params Request parameters including client_key, process_name, transaction_key
   * @returns Observable with transaction data
   */
  loadTransactions(baseUrl: string, params: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=l6gzle`;
    
    // Convert params to URLSearchParams for form-urlencoded format
    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }

  /**
   * Save Schedule F form data
   * @param baseUrl Base URL from GENERAL_CONFIG
   * @param mainData Array of form data to save
   * @param diagnosticData Array of diagnostic exception data
   * @param globalService Global service object with globalParams
   * @param filterParams Filter parameters from workspace
   * @param rowData Row data from grid
   * @returns Observable with save response
   */
  saveSchddForm(
    baseUrl: string, 
    mainData: any[], 
    diagnosticData: any[],
    globalService: any, 
    filterParams: any = {}, 
    rowData: any = {}
  ): Observable<any> {
    // Build payload EXACTLY like F1120L-SCH-B with proper fallbacks
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Essential parameters with fallbacks
    params.jsonSettings = JSON.stringify({
      "tax_year": rowData.TAX_YEAR,
      "scenario": rowData.SCENARIO,
      "jcd_key": rowData.JCD_KEY,
      "combination_key": rowData.COMBINATION_KEY,
      "trans_type_key": mainData[0].editrows[0][0].trans_type_key
    });
    params.jsonObj = JSON.stringify(mainData);
    params.tax_year = filterParams.tax_year || params.tax_year || rowData.TAX_YEAR;
    params.scenario = filterParams.scenario || params.scenario || rowData.SCENARIO;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || rowData.JCD_KEY || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";
    params.process_name = filterParams.process_name || "1120L Sch F";
    params.combination_key = rowData.COMBINATION_KEY;
    params.trans_type_key = mainData[0].editrows[0][0].trans_type_key;
    
    // Include checkLockedbj from rowData
    const checkLockedbj = [{
      "leid": rowData.LEID,
      "me_code": rowData.ME,
      "combination_key": rowData.COMBINATION_KEY
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    console.log('Save params being sent:', params);

    // MUST use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const url = `${baseUrl}/saveJsonObject?action_code=nkepph`;
    
    const formSave$ = this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
    
    // Save diagnostic data in parallel if provided
    if (diagnosticData && diagnosticData.length > 0) {
      const diagParams: any = {
        diag_exception: JSON.stringify(diagnosticData),
        tax_year: rowData.TAX_YEAR,
        screen_key: rowData.SCREEN_KEY || globalService?.globalParams?.screen_key || filterParams.screen_key || '',
        scenario: rowData.SCENARIO,
        jcd_key: rowData.JCD_KEY,
        scenarioCode: filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF",
        is_issue_key_enabled: "N"
      };

      const diagUrlEncoded = new URLSearchParams();
      Object.keys(diagParams).forEach(key => {
        if (diagParams[key] !== undefined && diagParams[key] !== null) {
          diagUrlEncoded.set(key, diagParams[key].toString());
        }
      });

      const diagUrl = `${baseUrl}/saveJsonObject?action_code=ftmltp`;
      
      // Execute diagnostic save in parallel (not blocking main save)
      this.http.post<any>(diagUrl, diagUrlEncoded.toString(), { headers, withCredentials: true })
        .subscribe({
          next: (diagResponse) => {
            console.log('Diagnostic data saved successfully:', diagResponse);
          },
          error: (diagError) => {
            console.error('Error saving diagnostic data:', diagError);
          }
        });
    }
    
    return formSave$;
  }

  /**
   * Save diagnostic data
   * @param baseUrl Base URL from GENERAL_CONFIG
   * @param params Request parameters including diag_exception, tax_year, screen_key, scenario, jcd_key
   * @returns Observable with save response
   */
  saveDiagnostic(baseUrl: string, params: any): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=ftmltp`;
    
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    // Convert params to URLSearchParams for form-urlencoded format
    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }
}

